/**
 * @fileoverview Site Message Tag Component
 *
 * A custom element that conditionally renders a site message based on session
 * storage keys. Skips evaluation in the theme editor (design mode), where the
 * editor handles preview rendering itself.
 *
 * @example
 * <site-message-tag>
 *   <script js-data type="application/json">{ "required_session_storage_key": ["…"] }</script>
 * </site-message-tag>
 */

/**
 * @classdesc Renders a site message when its required session storage keys are present.
 * @extends HTMLElement
 */
export class SiteMessageTag extends HTMLElement {
  #data;
  #can_render;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#data = {};
    this.#init();
  }

  #init = () => {
    this.#data = this.#setData();
    // if we are in the theme editor, go no further.
    // when in design mode, the theme editor code will show a message as it is edited.
    if(Shopify.designMode) return;
    this.#can_render = this.#evaluateSessionStorage();
    this.#updateRendering();
  }

  #setData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  getData = () => {
    return this.#data;
  }

  #evaluateSessionStorage = () => {
    const requiredKeys = this.#data.required_session_storage_key;
    if (!requiredKeys || requiredKeys.length === 0) {
      return true;
    }
    return requiredKeys.some(key => sessionStorage.getItem(key) !== null);
  }

  #updateRendering = () => {
    if (this.#can_render) {
      this.classList.add('loaded');
    } else {
      this.remove();
    }
  }
}

if (!customElements.get('site-message-tag')) {
  customElements.define('site-message-tag', SiteMessageTag);
}
